import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { api, AttendedEventMemberSummary } from '../../services/api';
import {
  AttendanceGeolocationError,
  getAttendanceCoordinatesWithRetry,
} from '../../../common/utils/attendanceGeolocation';

/**
 * Session-scoped variant of the legacy `EditAttendanceTimesDialog`. Edits
 * clock-in / clock-out times for members attributed to a specific
 * `TransformationTrainingSession`. The session's `SessionDate` is the base
 * calendar day; the user picks only the time-of-day.
 */

interface SessionEditAttendanceDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId: number | null;
  /** YYYY-MM-DD; used to combine with time-of-day into a full timestamp. */
  sessionDate: string;
  sessionName: string;
  attendedMembers: AttendedEventMemberSummary[];
  onRefresh: () => void;
}

const ROWS_PER_PAGE = 25;

type AttendanceTimeSortKey = 'clockedIn' | 'clockedOut';
type SortDirection = 'asc' | 'desc';

function toTimeOfDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function combineDateAndTime(sessionDate: string, timeOfDay: string): string {
  // sessionDate is YYYY-MM-DD in the app's local zone. Parse both parts so
  // the resulting ISO string reflects "sessionDate at HH:MM" in local time.
  if (!sessionDate || !timeOfDay) return '';
  const [y, mo, d] = sessionDate.slice(0, 10).split('-').map((v) => parseInt(v, 10));
  const [h, mi] = timeOfDay.split(':').map((v) => parseInt(v, 10));
  if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return '';
  return new Date(y, mo - 1, d, h, mi, 0, 0).toISOString();
}

function resolveAgentName(
  agentNames: Record<string, string> | undefined,
  ceaNumber: string,
): string {
  if (!agentNames) return '';
  const trimmed = ceaNumber.trim();
  if (!trimmed) return '';
  if (agentNames[trimmed]) return agentNames[trimmed];
  const upper = trimmed.toUpperCase();
  const matchedKey = Object.keys(agentNames).find((key) => key.toUpperCase() === upper);
  return matchedKey ? agentNames[matchedKey] : '';
}

function parseTimestampMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function compareNullableTimes(
  aMs: number | null,
  bMs: number | null,
  direction: SortDirection,
): number {
  if (aMs == null && bMs == null) return 0;
  if (aMs == null) return 1;
  if (bMs == null) return -1;
  return direction === 'asc' ? aMs - bMs : bMs - aMs;
}

const SessionEditAttendanceDialog: React.FC<SessionEditAttendanceDialogProps> = ({
  open,
  onClose,
  sessionId,
  sessionDate,
  sessionName,
  attendedMembers,
  onRefresh,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [sortBy, setSortBy] = useState<AttendanceTimeSortKey>('clockedIn');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [localTimes, setLocalTimes] = useState<
    Record<string, { clockedInAt: string; clockedOutAt: string }>
  >({});
  const [savingCea, setSavingCea] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [newAttendee, setNewAttendee] = useState<{
    ceaNumber: string;
    name: string;
    clockedInAt: string;
    clockedOutAt: string;
    loadingName?: boolean;
  } | null>(null);
  const lookupGenerationRef = useRef(0);
  const lastLookedUpCeaRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRowError({});
    setSuccessMessage(null);
    setLocationError(null);
    setNewAttendee(null);
    setSearchQuery('');
    setPage(0);
    setSortBy('clockedIn');
    setSortDirection('desc');
    lookupGenerationRef.current = 0;
    lastLookedUpCeaRef.current = null;
  }, [open]);

  useEffect(() => {
    const initial: Record<string, { clockedInAt: string; clockedOutAt: string }> = {};
    for (const m of attendedMembers) {
      initial[m.ceaNumber] = {
        clockedInAt: toTimeOfDay(m.clockedInAt ?? m.timeScanned),
        clockedOutAt: toTimeOfDay(m.clockedOutAt),
      };
    }
    setLocalTimes(initial);
  }, [attendedMembers]);

  const handleCeaLookup = async (cea: string) => {
    const trimmed = cea.trim().toUpperCase();
    if (!trimmed || sessionId == null) return;
    if (trimmed === lastLookedUpCeaRef.current) return;

    const generation = ++lookupGenerationRef.current;
    setNewAttendee((prev) => (prev ? { ...prev, loadingName: true } : null));
    try {
      const res = await api.getSessionAgentNames(sessionId, [trimmed]);
      if (generation !== lookupGenerationRef.current) return;

      const fetchedName = resolveAgentName(res.agentNames, trimmed);
      lastLookedUpCeaRef.current = trimmed;
      setNewAttendee((prev) => {
        if (!prev || prev.ceaNumber.trim().toUpperCase() !== trimmed) return prev;
        return { ...prev, name: fetchedName, loadingName: false };
      });
    } catch (err) {
      console.error('Failed to look up name by CEA:', err);
      if (generation !== lookupGenerationRef.current) return;
      setNewAttendee((prev) => {
        if (!prev || prev.ceaNumber.trim().toUpperCase() !== trimmed) return prev;
        return { ...prev, loadingName: false };
      });
    }
  };

  const captureLocation = async (): Promise<{ latitude: number | null; longitude: number | null }> => {
    try {
      const coords = await getAttendanceCoordinatesWithRetry();
      return { latitude: coords.latitude, longitude: coords.longitude };
    } catch (e) {
      console.warn('Failed to obtain admin location for attendance edit:', e);
      return { latitude: null, longitude: null };
    }
  };

  const handleSaveNewAttendee = async () => {
    if (!newAttendee || !newAttendee.ceaNumber.trim() || sessionId == null) return;
    const trimmedCea = newAttendee.ceaNumber.trim().toUpperCase();
    setSavingCea(trimmedCea);
    setRowError((prev) => ({ ...prev, [trimmedCea]: '' }));
    setLocationError(null);
    try {
      let latitude: number;
      let longitude: number;
      try {
        const coords = await getAttendanceCoordinatesWithRetry();
        latitude = coords.latitude;
        longitude = coords.longitude;
      } catch (geoError) {
        if (
          geoError instanceof AttendanceGeolocationError &&
          geoError.code === 'PERMISSION_DENIED'
        ) {
          setLocationError('Location must be enabled to add an attendee.');
          return;
        }
        throw geoError;
      }
      const clockedInAtIso = combineDateAndTime(sessionDate, newAttendee.clockedInAt);
      const clockedOutAtIso = combineDateAndTime(sessionDate, newAttendee.clockedOutAt);
      await api.updateSessionAttendedMemberTimes(sessionId, trimmedCea, {
        clockedInAt: clockedInAtIso || undefined,
        clockedOutAt: clockedOutAtIso || undefined,
        latitude,
        longitude,
      });
      setSuccessMessage(`Successfully added ${newAttendee.name || trimmedCea} to attendance!`);
      setNewAttendee(null);
      onRefresh();
    } catch (err) {
      console.error(err);
      setRowError((prev) => ({
        ...prev,
        [trimmedCea]: err instanceof Error ? err.message : 'Failed to add attendee',
      }));
    } finally {
      setSavingCea(null);
    }
  };

  const handleSaveRow = async (member: AttendedEventMemberSummary) => {
    if (sessionId == null) return;
    setSavingCea(member.ceaNumber);
    setRowError((prev) => ({ ...prev, [member.ceaNumber]: '' }));
    try {
      const times = localTimes[member.ceaNumber] || { clockedInAt: '', clockedOutAt: '' };
      const clockedInAtIso = combineDateAndTime(sessionDate, times.clockedInAt);
      const clockedOutAtIso = combineDateAndTime(sessionDate, times.clockedOutAt);
      const { latitude, longitude } = await captureLocation();
      await api.updateSessionAttendedMemberTimes(sessionId, member.ceaNumber, {
        clockedInAt: clockedInAtIso || undefined,
        clockedOutAt: clockedOutAtIso || undefined,
        latitude,
        longitude,
      });
      setSuccessMessage(`Successfully updated times for ${member.name || member.ceaNumber}!`);
      onRefresh();
    } catch (err) {
      console.error(err);
      setRowError((prev) => ({
        ...prev,
        [member.ceaNumber]: err instanceof Error ? err.message : 'Failed to update',
      }));
    } finally {
      setSavingCea(null);
    }
  };

  const filteredMembers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = !q
      ? attendedMembers
      : attendedMembers.filter(
          (m) =>
            (m.name || '').toLowerCase().includes(q) ||
            (m.ceaNumber || '').toLowerCase().includes(q),
        );
    return [...filtered].sort((a, b) => {
      const aMs =
        sortBy === 'clockedIn'
          ? parseTimestampMs(a.clockedInAt ?? a.timeScanned)
          : parseTimestampMs(a.clockedOutAt);
      const bMs =
        sortBy === 'clockedIn'
          ? parseTimestampMs(b.clockedInAt ?? b.timeScanned)
          : parseTimestampMs(b.clockedOutAt);
      return compareNullableTimes(aMs, bMs, sortDirection);
    });
  }, [attendedMembers, searchQuery, sortBy, sortDirection]);

  const handleSort = (key: AttendanceTimeSortKey) => {
    setPage(0);
    if (sortBy === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDirection('desc');
  };
  const paginatedMembers = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return filteredMembers.slice(start, start + ROWS_PER_PAGE);
  }, [filteredMembers, page]);

  const nowTime = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Edit Attendance</Typography>
            <Typography variant="caption" color="text.secondary">
              {sessionName ? `${sessionName} · ${sessionDate}` : sessionDate}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, minHeight: 400 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search by Name or CEA Number..."
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ flexGrow: 1, minWidth: 220 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                lookupGenerationRef.current += 1;
                lastLookedUpCeaRef.current = null;
                setNewAttendee({
                  ceaNumber: '',
                  name: '',
                  clockedInAt: nowTime(),
                  clockedOutAt: '',
                });
                setPage(0);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Add Attendee
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {searchQuery.trim()
              ? `${filteredMembers.length} of ${attendedMembers.length} attendee${attendedMembers.length === 1 ? '' : 's'}`
              : `${attendedMembers.length} attendee${attendedMembers.length === 1 ? '' : 's'}`}
          </Typography>
          {filteredMembers.length === 0 && !newAttendee ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No matching attended users found.
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>CEA Number</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell
                        sx={{ fontWeight: 600 }}
                        sortDirection={sortBy === 'clockedIn' ? sortDirection : false}
                      >
                        <TableSortLabel
                          active={sortBy === 'clockedIn'}
                          direction={sortBy === 'clockedIn' ? sortDirection : 'desc'}
                          onClick={() => handleSort('clockedIn')}
                        >
                          Clocked In
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600 }}
                        sortDirection={sortBy === 'clockedOut' ? sortDirection : false}
                      >
                        <TableSortLabel
                          active={sortBy === 'clockedOut'}
                          direction={sortBy === 'clockedOut' ? sortDirection : 'desc'}
                          onClick={() => handleSort('clockedOut')}
                        >
                          Clocked Out
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {page === 0 && newAttendee && (
                      <TableRow sx={{ bgcolor: 'rgba(var(--color-accent-rgb), 0.04)' }}>
                        <TableCell>
                          <TextField
                            placeholder="e.g. R012345A"
                            size="small"
                            value={newAttendee.ceaNumber}
                            onChange={(e) => {
                              const val = e.target.value;
                              const nextCea = val.trim().toUpperCase();
                              if (nextCea !== lastLookedUpCeaRef.current) {
                                lastLookedUpCeaRef.current = null;
                              }
                              setNewAttendee((prev) =>
                                prev ? { ...prev, ceaNumber: val, name: nextCea === prev.ceaNumber.trim().toUpperCase() ? prev.name : '' } : null,
                              );
                              if (nextCea.length === 8) {
                                void handleCeaLookup(val);
                              }
                            }}
                            onBlur={(e) => {
                              const nextCea = e.target.value.trim().toUpperCase();
                              if (nextCea.length >= 8 && nextCea !== lastLookedUpCeaRef.current) {
                                void handleCeaLookup(e.target.value);
                              }
                            }}
                            sx={{ width: 130 }}
                          />
                        </TableCell>
                        <TableCell>
                          {newAttendee.loadingName ? (
                            <CircularProgress size={14} />
                          ) : (
                            newAttendee.name || (
                              <Typography variant="caption" color="text.secondary">
                                Automatic lookup on CEA
                              </Typography>
                            )
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={newAttendee.clockedInAt}
                            onChange={(e) =>
                              setNewAttendee((prev) => (prev ? { ...prev, clockedInAt: e.target.value } : null))
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 140 }}
                            inputProps={{ step: 60 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={newAttendee.clockedOutAt}
                            onChange={(e) =>
                              setNewAttendee((prev) => (prev ? { ...prev, clockedOutAt: e.target.value } : null))
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 140 }}
                            inputProps={{ step: 60 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            {rowError[newAttendee.ceaNumber.trim().toUpperCase() || 'NEW_ATTENDEE_TEMP'] && (
                              <Tooltip title={rowError[newAttendee.ceaNumber.trim().toUpperCase() || 'NEW_ATTENDEE_TEMP']}>
                                <Typography variant="caption" color="error" sx={{ cursor: 'pointer', mr: 1 }}>
                                  Error
                                </Typography>
                              </Tooltip>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => setNewAttendee(null)}
                              sx={{ textTransform: 'none', fontWeight: 600, height: 30 }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => void handleSaveNewAttendee()}
                              disabled={
                                savingCea === (newAttendee.ceaNumber.trim().toUpperCase() || 'NEW_ATTENDEE_TEMP') ||
                                !newAttendee.ceaNumber.trim()
                              }
                              sx={{ minWidth: 60, height: 30, textTransform: 'none', fontWeight: 600 }}
                            >
                              {savingCea === (newAttendee.ceaNumber.trim().toUpperCase() || 'NEW_ATTENDEE_TEMP') ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                'Save'
                              )}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                    {paginatedMembers.map((member) => {
                      const times = localTimes[member.ceaNumber] || { clockedInAt: '', clockedOutAt: '' };
                      const isSaving = savingCea === member.ceaNumber;
                      const hasError = rowError[member.ceaNumber];
                      return (
                        <TableRow key={member.ceaNumber}>
                          <TableCell>{member.ceaNumber}</TableCell>
                          <TableCell>{member.name || '-'}</TableCell>
                          <TableCell>
                            <TextField
                              type="time"
                              size="small"
                              value={times.clockedInAt}
                              onChange={(e) =>
                                setLocalTimes((prev) => ({
                                  ...prev,
                                  [member.ceaNumber]: {
                                    ...(prev[member.ceaNumber] || { clockedInAt: '', clockedOutAt: '' }),
                                    clockedInAt: e.target.value,
                                  },
                                }))
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 140 }}
                              inputProps={{ step: 60 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="time"
                              size="small"
                              value={times.clockedOutAt}
                              onChange={(e) =>
                                setLocalTimes((prev) => ({
                                  ...prev,
                                  [member.ceaNumber]: {
                                    ...(prev[member.ceaNumber] || { clockedInAt: '', clockedOutAt: '' }),
                                    clockedOutAt: e.target.value,
                                  },
                                }))
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 140 }}
                              inputProps={{ step: 60 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                              {hasError && (
                                <Tooltip title={hasError}>
                                  <Typography variant="caption" color="error" sx={{ cursor: 'pointer', mr: 1 }}>
                                    Error
                                  </Typography>
                                </Tooltip>
                              )}
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => void handleSaveRow(member)}
                                disabled={isSaving}
                                sx={{ minWidth: 60, height: 30, textTransform: 'none', fontWeight: 600 }}
                              >
                                {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredMembers.length > ROWS_PER_PAGE && (
                <TablePagination
                  component="div"
                  count={filteredMembers.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                />
              )}
            </>
          )}
          {sessionId == null && (
            <Alert severity="warning">Session not selected.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(locationError)}
        autoHideDuration={6000}
        onClose={() => setLocationError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }} onClose={() => setLocationError(null)}>
          {locationError}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default SessionEditAttendanceDialog;
